// src/utils/authUtils.js

import { listRoles } from '../Api';

/**
 * Auth utility functions for cctv_analyzer_frontend
 * Reads auth data set by white-tiger-landing login/signup
 */

export const AUTH_UPDATE_EVENT = 'auth-data-updated';

const ROLE_METADATA_PREFIX = 'roleMetadata:';
const ROLE_METADATA_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_PROD_LOGIN_URL = 'https://www.whitetiger.ai/login/';
const DEFAULT_LOCAL_LOGIN_URL = 'http://localhost:3001/login/';

let roleMetadataCache = null;
let roleMetadataOrgId = null;
let roleMetadataPromise = null;

const getSafeStorage = () => (typeof window === 'undefined' ? null : window.localStorage);

const getRoleMetadataKey = (orgId) => `${ROLE_METADATA_PREFIX}${orgId}`;

const metadataIsFresh = (metadata) => {
  if (!metadata || !metadata.fetchedAt) return false;
  return Date.now() - metadata.fetchedAt < ROLE_METADATA_TTL_MS;
};

const loadMetadataFromStorage = (orgId) => {
  const storage = getSafeStorage();
  if (!storage || !orgId) return null;

  try {
    const raw = storage.getItem(getRoleMetadataKey(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return metadataIsFresh(parsed) ? parsed : null;
  } catch (error) {
    console.warn('[authUtils] Failed to parse role metadata from storage:', error);
    storage.removeItem(getRoleMetadataKey(orgId));
    return null;
  }
};

const cacheMetadata = (orgId, metadata) => {
  const storage = getSafeStorage();
  if (!storage || !orgId) return;

  roleMetadataCache = metadata;
  roleMetadataOrgId = metadata ? orgId : null;

  if (!metadata) {
    storage.removeItem(getRoleMetadataKey(orgId));
    return;
  }

  const payload = {
    ...metadata,
    fetchedAt: metadata.fetchedAt || Date.now(),
  };

  try {
    storage.setItem(getRoleMetadataKey(orgId), JSON.stringify(payload));
  } catch (error) {
    console.warn('[authUtils] Failed to cache role metadata:', error);
  }
};

const getCachedMetadata = (orgId) => {
  if (!orgId) return null;

  if (roleMetadataCache && roleMetadataOrgId === orgId && metadataIsFresh(roleMetadataCache)) {
    return roleMetadataCache;
  }

  const stored = loadMetadataFromStorage(orgId);
  if (stored) {
    roleMetadataCache = stored;
    roleMetadataOrgId = orgId;
    return stored;
  }

  return null;
};

const clearRoleMetadataCache = () => {
  roleMetadataCache = null;
  roleMetadataOrgId = null;
  roleMetadataPromise = null;
};

export const invalidateRoleMetadataCache = () => {
  const storage = getSafeStorage();
  if (storage) {
    try {
      const keysToRemove = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key && key.startsWith(ROLE_METADATA_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => storage.removeItem(key));
    } catch (error) {
      console.warn('[authUtils] Failed to clear cached role metadata from storage:', error);
    }
  }
  clearRoleMetadataCache();
};

export const notifyAuthDataUpdated = ({ invalidateMetadata = false } = {}) => {
  if (invalidateMetadata) {
    invalidateRoleMetadataCache();
  }

  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new Event(AUTH_UPDATE_EVENT));
    } catch (error) {
      console.warn('[authUtils] Failed to dispatch auth update event:', error);
    }
  }
};

export const getAccessToken = () => {
  const storage = getSafeStorage();
  return storage ? storage.getItem('accessToken') : null;
};

const decodeAccessTokenPayload = () => {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const segments = token.split('.');
    if (segments.length < 2) return null;

    const payloadSegment = segments[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedPayload =
      payloadSegment + '='.repeat((4 - (payloadSegment.length % 4)) % 4);

    if (typeof atob !== 'function') {
      console.warn('[authUtils] atob is not available in this environment');
      return null;
    }

    const decoded = atob(paddedPayload);
    const payload = JSON.parse(decoded);

    if (payload?.organization_id) {
      const storage = getSafeStorage();
      if (storage) {
        const storedOrgId = storage.getItem('organizationId');
        if (storedOrgId !== payload.organization_id) {
          try {
            storage.setItem('organizationId', payload.organization_id);
          } catch (error) {
            console.warn('[authUtils] Failed to persist organizationId from token payload:', error);
          }
        }
      }
    }

    return payload;
  } catch (error) {
    console.error('[authUtils] Failed to decode access token:', error);
    return null;
  }
};

export const getOrganizationId = () => {
  const storage = getSafeStorage();
  return storage ? storage.getItem('organizationId') : null;
};

export const getLogoutRedirectUrl = () => {
  const envUrl = process.env.REACT_APP_LOGIN_URL;
  if (envUrl && envUrl.trim()) {
    return envUrl.trim();
  }

  if (typeof window === 'undefined') {
    return DEFAULT_PROD_LOGIN_URL;
  }

  const { location } = window;
  const hostname = location.hostname.toLowerCase();

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return DEFAULT_LOCAL_LOGIN_URL;
  }

  if (hostname.endsWith('.whitetiger.ai')) {
    return DEFAULT_PROD_LOGIN_URL;
  }

  return `${location.origin.replace(/\/+$/, '')}/login`;
};

export const getUserRoles = () => {
  const storage = getSafeStorage();
  if (!storage) return [];

  const rolesJson = storage.getItem('userRoles');
  console.log('[authUtils] getUserRoles() - raw value:', rolesJson);

  if (!rolesJson) {
    console.log('[authUtils] getUserRoles() - no roles found in localStorage');
  } else {
    try {
      const parsed = JSON.parse(rolesJson);
      console.log('[authUtils] getUserRoles() - parsed value:', parsed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch (e) {
      console.error('[authUtils] Failed to parse userRoles from localStorage:', e);
      storage.removeItem('userRoles');
    }
  }

  // Fallback to decoding JWT payload if localStorage is empty or invalid
  const payload = decodeAccessTokenPayload();
  const tokenRolesRaw = payload?.user_roles;

  let rolesFromToken = [];
  if (Array.isArray(tokenRolesRaw)) {
    rolesFromToken = tokenRolesRaw;
  } else if (typeof tokenRolesRaw === 'string' && tokenRolesRaw.trim()) {
    rolesFromToken = [tokenRolesRaw.trim()];
  }

  if (rolesFromToken.length > 0) {
    console.log('[authUtils] getUserRoles() - recovered roles from access token', rolesFromToken);
    try {
      storage.setItem('userRoles', JSON.stringify(rolesFromToken));
    } catch (error) {
      console.warn('[authUtils] Failed to persist recovered roles to localStorage:', error);
    }
    notifyAuthDataUpdated({ invalidateMetadata: true });
    return rolesFromToken;
  }

  console.log('[authUtils] getUserRoles() - no roles available after token fallback');
  return [];
};

export const getUserEmail = () => {
  const payload = decodeAccessTokenPayload();
  return payload?.sub || null;
};

const fetchRoleMetadata = async () => {
  const orgId = getOrganizationId();
  if (!orgId) return null;

  const cached = getCachedMetadata(orgId);
  if (cached) return cached;

  if (!roleMetadataPromise) {
    roleMetadataPromise = (async () => {
      try {
        const roles = await listRoles();
        const metadata = {
          roles: Array.isArray(roles) ? roles : [],
          fetchedAt: Date.now(),
        };
        cacheMetadata(orgId, metadata);
        return metadata;
      } catch (error) {
        console.error('[authUtils] Failed to load role metadata:', error);
        cacheMetadata(orgId, null);
        return null;
      } finally {
        roleMetadataPromise = null;
      }
    })();
  }

  return roleMetadataPromise;
};

const ensureRoleMetadata = async () => {
  const orgId = getOrganizationId();
  if (!orgId) return null;

  const cached = getCachedMetadata(orgId);
  if (cached) return cached;

  return fetchRoleMetadata();
};

const getUserRoleIds = () => {
  const roles = getUserRoles();
  return Array.isArray(roles) ? roles : [];
};

const ADMIN_PERMISSION_KEYS = [
  'role:write',
  'role:delete',
  'organization:write',
  'organization:delete',
  'organization:all',
  'user:write',
  'user:delete',
  'super:admin',
];

const roleHasAdminPrivileges = (role) => {
  if (!role) return false;
  const name = (role.name || '').toLowerCase();
  if (name === 'admin' || name === 'super admin') {
    return true;
  }

  const permissions = Array.isArray(role.permissions) ? role.permissions : [];
  return permissions.some((permission) => ADMIN_PERMISSION_KEYS.includes(permission));
};

const roleMatchesName = (role, requiredRole) => {
  if (!role || !requiredRole) return false;
  const normalizedRequired = requiredRole.trim().toLowerCase();
  const roleName = (role.name || '').trim().toLowerCase();

  if (!roleName) return false;
  if (normalizedRequired === 'admin') {
    return roleName === 'admin' || roleName === 'super admin' || roleHasAdminPrivileges(role);
  }

  if (normalizedRequired === 'super admin') {
    return roleName === 'super admin' || role.permissions?.includes('super:admin') || role.permissions?.includes('organization:all');
  }

  return roleName === normalizedRequired;
};

const getMatchingRoles = (metadata, roleIds) => {
  if (!metadata?.roles || !Array.isArray(roleIds) || roleIds.length === 0) {
    return [];
  }

  return metadata.roles.filter((role) => roleIds.includes(role.role_id));
};

export const hasRole = (roleName) => {
  const roleIds = getUserRoleIds();
  if (!roleIds.length || !roleName) return false;

  const orgId = getOrganizationId();
  const metadata = getCachedMetadata(orgId);
  if (!metadata) return false;

  return getMatchingRoles(metadata, roleIds).some((role) => roleMatchesName(role, roleName));
};

export const userHasRole = async (roleName) => {
  if (!roleName) return false;
  if (hasRole(roleName)) return true;

  const roleIds = getUserRoleIds();
  if (!roleIds.length) return false;

  const metadata = await ensureRoleMetadata();
  if (!metadata) return false;

  return getMatchingRoles(metadata, roleIds).some((role) => roleMatchesName(role, roleName));
};

export const isAdmin = () => hasRole('Admin');

export const hasAdminAccess = async () => {
  if (isAdmin()) return true;
  return userHasRole('Admin');
};

export const isSuperAdmin = () => hasRole('Super Admin');

export const hasSuperAdminAccess = async () => {
  if (isSuperAdmin()) return true;
  return userHasRole('Super Admin');
};

export const isAuthenticated = () => {
  return !!getAccessToken();
};

export const clearAuth = () => {
  const storage = getSafeStorage();
  if (!storage) return;

  // SECURITY FIX: Clear ALL localStorage data on logout
  // This prevents data leakage on shared devices by removing:
  // - Authentication tokens (accessToken, refreshToken, etc.)
  // - User-specific cached data (idleSnapshot:*, persistedAlertDisplay:*, etc.)
  // - Session data (redirectAfterLogin, role metadata, etc.)
  // See: USER_MANAGEMENT_FIX.md - Issue 3
  storage.clear();

  // Clear in-memory caches
  clearRoleMetadataCache();

  // Notify components that auth data has been cleared
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new Event(AUTH_UPDATE_EVENT));
    } catch (error) {
      console.warn('[authUtils] Failed to dispatch auth update event:', error);
    }
  }
};
